import React from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { FinalCTA } from "@/pages/Home";
import { api } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import Seo from "@/components/Seo";
import { mediaUrl } from "@/lib/api";

export default function PortfolioDetail() {
  const { t, isAr } = useLang();
  const { slug } = useParams();
  const { data: c, isLoading, isError } = useQuery({
    queryKey: ["portfolio-detail", slug],
    queryFn: () => api.get(`/portfolio/${slug}`).then((r) => r.data),
  });

  if (isLoading) return <div className="pt-40 container-x text-muted-text">{t("Loading…")}</div>;
  if (isError || !c) return (
    <div className="pt-40 pb-40 container-x text-center">
      <h1 className="text-3xl font-light text-ivory">{t("Company not found.")}</h1>
      <Link to="/portfolio" className="btn-gold mt-8">{t("Back to Portfolio")}</Link>
    </div>
  );

  const name = isAr && c.name_ar ? c.name_ar : c.name;
  const overview = isAr && c.overview_ar ? c.overview_ar : c.overview;
  const description = isAr && c.description_ar ? c.description_ar : c.description;
  const intersectRole = isAr && c.intersect_role_ar ? c.intersect_role_ar : c.intersect_role;
  const meta = [["Sector", c.sector], ["Geography", c.location], ["Relationship", c.relationship_type], ["Year", c.year], ["Status", c.status]];
  return (
    <>
      <Seo
        title={`${name} — Intersect Holding`}
        description={(overview || description || `${name}, a venture in the Intersect Holding portfolio.`).slice(0, 160)}
        path={`/portfolio/${slug}`}
        image={c.image_url || undefined}
        type="article"
        lang={isAr ? "ar" : "en"}
      />
      <section className="pt-36 pb-16 md:pb-24">
        <div className="container-x">
          <Link to="/portfolio" className="link-underline text-sm tracking-wide uppercase mb-12"><ArrowLeft size={16} strokeWidth={1.5} /> {t("Portfolio")}</Link>
          <Reveal><p className="eyebrow mb-6 mt-8">{t(c.relationship_type)}</p></Reveal>
          <Reveal delay={0.1}><h1 className="text-4xl md:text-6xl font-light tracking-tight text-ivory">{name}</h1></Reveal>
          {c.image_url && <Reveal delay={0.2}><img src={mediaUrl(c.image_url)} alt={name} className="mt-12 w-full aspect-[16/7] object-cover" /></Reveal>}
        </div>
      </section>

      <section className="pb-24">
        <div className="container-x grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7 space-y-10">
            {overview && <div><p className="eyebrow mb-4">{t("Company Overview")}</p><p className="text-lg leading-relaxed text-soft-stone">{overview}</p></div>}
            {description && <div><p className="eyebrow mb-4">{t("Opportunity")}</p><p className="text-lg leading-relaxed text-soft-stone">{description}</p></div>}
            {intersectRole && <div><p className="eyebrow mb-4">{t("Intersect Role")}</p><p className="text-lg leading-relaxed text-soft-stone">{intersectRole}</p></div>}
          </div>
          <div className="lg:col-span-4 lg:col-start-9">
            <div className="border-t border-primary-gold/15">
              {meta.filter(([, v]) => v).map(([k, v]) => (
                <div key={k} className="flex items-center justify-between py-4 border-b border-white/5">
                  <span className="text-xs tracking-wide uppercase text-muted-text">{t(k)}</span>
                  <span className="text-sm text-ivory">{t(v)}</span>
                </div>
              ))}
            </div>
            {c.website && <a href={c.website} target="_blank" rel="noreferrer" className="btn-gold mt-8 w-full">{t("Visit Website")} <ArrowUpRight size={16} strokeWidth={1.5} /></a>}
          </div>
        </div>
      </section>
      <FinalCTA />
    </>
  );
}
